import React from 'react';
import {isMobile, isFirefox} from 'react-device-detect';
import * as Sentry from '@sentry/react';
import WebcamType from 'react-webcam';
import {isMobileOrTablet} from 'utils/mobile';
import scanAreaImage from 'assets/scan-area.svg';
import {StyledWebcam, Wrapper, ScanArea} from './styled';

const SCREENSHOT_FORMAT = 'image/jpeg';
const MEDIA_CONSTRAINTS = {
  audio: false,
  video: {
    width: {ideal: 4096},
    height: {ideal: 2160},
  },
};

function getMobileOrDesktopVideoConstraints() {
  if (isMobileOrTablet()) {
    return {
      width: {min: 576, ideal: 1700, max: 2160},
      height: {min: 1024, ideal: 3200, max: 4096},
      facingMode: {
        exact: 'environment',
      },
    };
  }
  return {
    facingMode: 'user',
  };
}

export interface WebcamRefTypes extends WebcamType {}

type WebcamProps = {
  onUserMedia?: () => void;
  onUserMediaError?: () => void;
  audio?: boolean;
  imageSmoothing?: boolean;
  mirrored?: boolean;
  screenshotQuality?: number;
  videoConstraints?: any;
  children?: React.ReactNode | JSX.Element;
  hideScanArea?: boolean;
  className?: string;
  minScreenshotH?: number;
  minScreenshotW?: number;
};

const Webcam = React.memo(
  React.forwardRef<WebcamRefTypes, WebcamProps>(
    (
      {
        onUserMedia,
        onUserMediaError,
        audio,
        imageSmoothing,
        children,
        videoConstraints,
        hideScanArea,
        className,
        mirrored,
        screenshotQuality = 1,
        minScreenshotH,
        minScreenshotW,
      },
      ref,
    ) => {
      const [minScreenshotHeight, setMinScreenshotHeight] = React.useState<number>();
      const [minScreenshotWidth, setMinScreenshotWidth] = React.useState<number>();
      const constraints = videoConstraints || getMobileOrDesktopVideoConstraints();
      const isMirroredInCSS = mirrored === undefined && !isMobileOrTablet();

      React.useEffect(() => {
        if (!isMobile) {
          try {
            window.navigator?.mediaDevices
              ?.getUserMedia(MEDIA_CONSTRAINTS)
              ?.then(stream => {
                const track = stream.getVideoTracks()[0];
                const aspectRatio = !isFirefox && track?.getCapabilities();

                if (aspectRatio) {
                  setMinScreenshotHeight(aspectRatio?.height?.max);
                  setMinScreenshotWidth(aspectRatio?.width?.max);
                }

                track.stop();
              });
          } catch (err) {
            Sentry.captureException(err);
          }
        } else {
          setMinScreenshotWidth(minScreenshotW);
          setMinScreenshotHeight(minScreenshotH);
        }
      }, [constraints, minScreenshotH, minScreenshotW]);

      return (
        <>
          <Wrapper className={className} isMirroredInCSS={isMirroredInCSS}>
            {!hideScanArea && <ScanArea src={scanAreaImage} alt="Scan area" />}
            <StyledWebcam
              ref={ref}
              onUserMedia={onUserMedia}
              onUserMediaError={onUserMediaError}
              imageSmoothing={imageSmoothing}
              mirrored={mirrored}
              screenshotQuality={screenshotQuality}
              videoConstraints={constraints}
              screenshotFormat={SCREENSHOT_FORMAT}
              minScreenshotHeight={minScreenshotHeight}
              minScreenshotWidth={minScreenshotWidth}
            />
          </Wrapper>
        </>
      );
    },
  ),
);

export {Webcam};
